import { useMutation } from '@apollo/client';
import { PermissionName } from '@sironamedical/common';
import { useToasterDispatch } from 'common/ui/Toaster';
import { ADD_EXAM_COMMENT, EXAM_COMMENTS } from 'modules/Apollo/queries';
import { logger } from 'modules/logger';
import { useCallback } from 'react';
import { useValidateUserHasRbacPermissions } from 'hooks/useValidateUserHasRbacPermissions';
import { WithApolloContext } from 'modules/Apollo/types';

export const useExamComments = ({ context }: WithApolloContext) => {
  const [addExamCommentMutation] = useMutation(ADD_EXAM_COMMENT, {
    context,
  });
  const hasWriteAccess = useValidateUserHasRbacPermissions({
    permissionNames: [PermissionName.ExamCommentReadWriteAccess],
  });
  const { enqueueToast } = useToasterDispatch();

  const addExamComment = useCallback(
    async ({
      worklistItemSmid,
      comment,
      onComplete,
    }: {
      worklistItemSmid: string;
      comment: string;
      onComplete?: () => void;
    }) => {
      // Comment text is free text and can hold PHI, log its length only
      logger.info('[useExamComments] Attempting to send exam comment', {
        worklistItemSmid,
        commentLength: comment.length,
      });

      if (!hasWriteAccess) {
        logger.warn('[useExamComments] User does not have permission to add exam comments', {
          worklistItemSmid,
        });
        enqueueToast('You do not have permission to add comments to this exam', {
          icon: 'redWarning',
        });
        return;
      }

      await addExamCommentMutation({
        variables: {
          worklistItemSmid,
          comment,
        },
        update(cache, { data }) {
          const comments = data?.addExamComment;

          if (!comments) {
            return;
          }

          cache.writeQuery({
            query: EXAM_COMMENTS,
            variables: { worklistItemSmid },
            data: { examComments: comments },
          });
        },
        onCompleted: () => {
          logger.info('[ExamCommentsTab] Successfully added exam comment', {
            worklistItemSmid,
            commentLength: comment.length,
          });
          onComplete?.();
        },
        onError: (error) => {
          // Not the error message, it comes from the backend response and can echo the comment text
          logger.error('[ExamCommentsTab] Error occurred when adding exam comment', {
            worklistItemSmid,
            commentLength: comment.length,
            errorCodes: error.graphQLErrors.map((graphQLError) => graphQLError.extensions?.code),
            hasNetworkError: error.networkError != null,
          });
          enqueueToast('An error occurred while adding your comment. Please try again.', {
            icon: 'redWarning',
          });
        },
      });
    },
    [addExamCommentMutation, enqueueToast, hasWriteAccess]
  );

  return {
    addExamComment,
  };
};
